import React, { useState, useEffect } from 'react';
import './StudentLayout.css';
import { useNavigate, NavLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { auth } from "../../firebase/firebase-config";
import { useAuthState } from 'react-firebase-hooks/auth';
import { getUserInfo } from '../../utils/storage';
import { logout } from '../../utils/auth';

// Icons grouped by category
import { MdDashboard, MdFeaturedPlayList } from 'react-icons/md';
import { CiMedicalCase, CiMedicalClipboard, CiUser, CiFolderOn, CiMedicalCross, CiMapPin, CiMedicalMask } from 'react-icons/ci';
import { FaPills, FaHospitalUser, FaUserMd, FaHeartbeat, FaFileMedical, FaClipboardList } from 'react-icons/fa';
import { AiFillDollarCircle, AiOutlineMenuFold } from 'react-icons/ai';
import { SiPlanet } from "react-icons/si";
import { GiTestTubes } from 'react-icons/gi';
import { BiCategoryAlt, BiAnalyse } from 'react-icons/bi';
import { RiHospitalLine } from 'react-icons/ri';

// Navigation items grouped by category
const navItems = [
  {
    category: "Main",
    items: [
      { name: "Dashboard", path: "/dashboard", icon: <MdDashboard size={18}/> }
    ]
  },
  {
    category: "Patients & Cases",
    items: [
      { name: "Patients", path: "/patients", icon: <FaHospitalUser size={18}/> },
      { name: "Blood Sugar Patients", path: "/patient-chart", icon: <FaHeartbeat size={18}/> },
      { name: "HBP Cases", path: "/hbp-cases", icon: <CiMedicalCross size={18}/> },
      { name: "Cases", path: "/cases", icon: <CiMedicalCase size={18}/> },
    ]
  },
  {
    category: "Medical Staff",
    items: [
      { name: "Doctors", path: "/", icon: <FaUserMd size={18}/> },
      { name: "Specialities", path: "/specialities", icon: <BiCategoryAlt size={18}/> },
    ]
  },
  {
    category: "Billing & Plans",
    items: [
      { name: "Subscriptions", path: "/manual-subs", icon: <AiFillDollarCircle size={18}/> },
      { name: "Plans", path: "/plans", icon: <SiPlanet size={18}/> },
      { name: "Features", path: "/features", icon: <MdFeaturedPlayList size={18}/> },
    ]
  },
  {
    category: "Medical Resources",
    items: [
      { name: "Slides", path: "/slides", icon: <CiFolderOn size={18}/> },
      { name: "Reports", path: "/report", icon: <CiMedicalClipboard size={18}/> },
      { name: "Screening", path: "/screening", icon: <FaClipboardList size={18}/> },
      { name: "Medications", path: "/medications", icon: <FaPills size={18}/> },
      { name: "Lab Tests", path: "/labtests", icon: <GiTestTubes size={18}/> },
      { name: "Radiology Exam", path: "/radiology-exam", icon: <GiTestTubes size={18}/> },
    ]
  },
  {
    category: "Facilities",
    items: [
      { name: "Facility Map", path: "/facility-map", icon: <CiMapPin size={18}/> },
      { name: "Facilities", path: "/facilities", icon: <RiHospitalLine size={18}/> },
      { name: "Facility Tags", path: "/facility-tags", icon: <BiCategoryAlt size={18}/> },
      { name: "Facility Types", path: "/facility-types", icon: <BiCategoryAlt size={18}/> },
      { name: "Facility Services", path: "/facility-services", icon: <FaFileMedical size={18}/> },
    ]
  },
  {
    category: "Other",
    items: [
      { name: "Products", path: "/products", icon: <CiMedicalClipboard size={18}/> },
      { name: "Ads", path: "/ads", icon: <CiMedicalClipboard size={18}/> },
      { name: "Insurances", path: "/insurances", icon: <CiMedicalClipboard size={18}/> },
      { name: "Tags Category", path: "/tags-categories", icon: <BiCategoryAlt size={18}/> },
      { name: "Action Logs", path: "/action-logs", icon: <BiAnalyse size={18}/> },
    ]
  }
];

function Layout({ title, children, pageTitle } : any) {
    const { t, i18n } = useTranslation();
    const currentUser = auth.currentUser;
    const [lang, setLang] = useState<any>(null);
    const navigate = useNavigate();
    const [user] = useAuthState(auth);
    const [showStudNav, setShowStudNav] = useState(true);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isLab, setIsLab] = useState<any>(null);
    const [expandedCategories, setExpandedCategories] = useState<any>({});

    const handleLogout = () => {
        logout().then(() => {
            navigate('/login');
        });
    }

    const toggleNav = () => {
        setShowStudNav(!showStudNav);
    }

    const toggleCategory = (category: any) => {
        setExpandedCategories((prev: any) => ({
            ...prev,
            [category]: !prev[category]
        }));
    }

    const handleTrans = () => {
        i18n.changeLanguage(lang);
    };
  
    const handleLangInit = () => {
        let lng = localStorage.getItem('locale');
        if(lng == null) {
            localStorage.setItem('locale', 'fr');
            setLang('fr');
        } else {
            setLang(lng);
        }
    }
  
    const changeLang = () => {
        if(lang != null) {
            localStorage.setItem('locale', lang);
            handleTrans();
        }
    }
      
    useEffect(() => {
        handleLangInit();
    }, []);
  
    useEffect(() => {
        changeLang();
    }, [lang]);

    useEffect(() => {
        checkAdmin();
    }, []);
 
    const checkAdmin = async() => {
        let data = await getUserInfo(user?.email);

        if(data?.role == 'admin') {
            setIsLab(false);
            
            // Initialize all categories as expanded
            const initialExpandedState: any = {};
            navItems.forEach(navGroup => {
                initialExpandedState[navGroup.category] = true;
            });
            setExpandedCategories(initialExpandedState);
        } else if(data?.role == 'lab') {
            navigate('/lab/home');
        } else {
            logout().then(() => {
                navigate('/login');
            });
        }
    }

    // if(isLab == null) {
    //     return <div className="loading-container"><div className="loader"></div><p>Loading dashboard...</p></div>;
    // }

    return (
        <div className="dashboard-grid">
            <div className={`sidebar-student student student-dashboard-sidebar ${!showStudNav ? 'show' : ''}`}>
                <div className="logo" style={{cursor: 'pointer'}}>
                    <a onClick={() => navigate('/')}>
                        <img src={require('../../assets/images/logo/logo-light.png')} alt="Logo" />
                    </a>
                </div>
                <div className="menu">
                    <div className="sidebar-user-info">
                        <div className="sidebar-avatar">
                            {currentUser?.displayName?.charAt(0) || "A"}
                        </div>
                        <div className="sidebar-user-name">
                            {currentUser?.displayName || "Admin User"}
                        </div>
                    </div>
                    
                    <div className="sub-menu">
                        {navItems.map((navGroup, groupIndex) => (
                            <div key={groupIndex} className="nav-category">
                                <div 
                                    className="category-header" 
                                    onClick={() => toggleCategory(navGroup.category)}
                                >
                                    <span>{navGroup.category}</span>
                                    <span className={`category-arrow ${expandedCategories[navGroup.category] ? 'expanded' : ''}`}>
                                        ▼
                                    </span>
                                </div>
                                
                                <div className={`category-items ${expandedCategories[navGroup.category] ? 'expanded' : ''}`}>
                                    {navGroup.items.map((item, itemIndex) => (
                                        <NavLink 
                                            key={itemIndex} 
                                            className="link" 
                                            to={item.path}
                                        >
                                            <i>{item.icon}</i>
                                            <span>{item.name}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        ))}
                        
                        <div className="external-links">
                            <a className="link" href="https://med-doc-chat.vercel.app/" target="_blank">
                                <i><CiMedicalMask size={18}/></i>
                                <span>Chat</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className={`main ${!showStudNav ? 'expand' : ''}`}>
                <header className={`${!showStudNav ? 'expand' : ''}`}>
                    <div className="con">
                        <div className="left-header">
                            <div className="nav-toggler-btn" onClick={toggleNav}>
                                <AiOutlineMenuFold />
                            </div>
                            <div className="page-title-small">
                                {pageTitle || title}
                            </div>
                        </div>
                        
                        <div className="header-controls">
                            <select 
                                value={lang} 
                                onChange={(e) => setLang(e.target.value)} 
                                className="language-dashboard"
                            >
                                <option value="en">🏴󠁧󠁢󠁥󠁮󠁧󠁿 EN</option>
                                <option value="fr">🇫🇷 FR</option>
                            </select>
                            
                            <div className="divider"></div>
                            
                            <a href="" className="link notify">
                                <i className="fa fa-bell" aria-hidden="true"></i>
                            </a>
                            
                            <div className="divider"></div>
                            
                            <div className="profile-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
                                <div className="profile-avatar">
                                    {currentUser?.displayName?.charAt(0) || "A"}
                                </div>
                                <div className="name">{currentUser?.displayName}</div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className={`user-menu ${showUserMenu ? 'show' : ''}`}>
                    <div className="user-menu-top">
                        <i className="fa fa-times" onClick={() => setShowUserMenu(!showUserMenu)}></i>
                        <div className="menu-avatar">
                            {currentUser?.displayName?.charAt(0) || "A"}
                        </div>
                        <p>{currentUser?.displayName || "Admin User"}</p>
                        <span>{currentUser?.email}</span>
                    </div>
                    <div className="user-menu-footer">
                        <a><i className="fas fa-cog"></i> Settings</a>
                        <a onClick={handleLogout} className="logout-link"><i className="fas fa-door-open"></i> Logout</a>
                    </div>
                </div>

                <div className="content">
                    <div className="con">
                        <div className="page-title">
                            <p>{title}</p>
                            <div className="crumb">
                                <NavLink to="/dashboard" className="crumb-item">{'Dashboard'}</NavLink>
                                <span>{'>'}</span>
                                <a className="crumb-item">{ pageTitle }</a>
                            </div>
                        </div>
                        <div className="section">
                            { children }
                        </div>
                    </div>
                </div>

                <footer className={`${!showStudNav ? 'expand' : ''}`}>
                    <div className="con">
                        <p style={{fontSize:12}}>{t('copywright_text')}</p>
                        <p style={{fontSize:12}}>Version 1.0</p>
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default Layout;